/*------------------------------------------------------------------------------
   About      : Bulk action bar for the NBI Insights list (epic DA-697,
                II-FE-2 selection model / II-FE-5 mutations)

                Each action is gated on its own BFF feature: proxypulse splits
                read / write / publish and publish + unpublish are
                CONTENT_PUBLISHERS-only, so a non-publisher sees the button
                hidden rather than getting a 403 on click.

                Outcomes are reported three ways, not two. A batch collects
                per-item failures and one bad row does not fail the batch, so
                "7 of 9 published; 2 failed" is a real state that must not
                render as either total success or total failure.

   Copyright (c) 2026 Bidgely. All rights reserved.
------------------------------------------------------------------------------*/
import { Alert, AlertTitle, Box, Button, CircularProgress, Stack,
         Typography } from '@mui/material'
import PublishIcon      from '@mui/icons-material/Publish'
import UnpublishedIcon  from '@mui/icons-material/Unpublished'
import DeleteSweepIcon  from '@mui/icons-material/DeleteSweep'

import { UI_PAGES }       from 'framework/access/UiRegistry'
import { PermissionGate } from 'components/access/PermissionGate'
import { BatchOutcome }   from '../utils/insightBatchResult'

interface InsightBulkActionBarProps {
  selectedCount     : number
  isRunning        ?: boolean
  outcome          ?: BatchOutcome
  onDismissOutcome ?: () => void
  onClear           : () => void
  onPublish        ?: () => void
  onUnpublish      ?: () => void
  onDiscard        ?: () => void
}

export const InsightBulkActionBar = ({
  selectedCount,
  isRunning,
  outcome,
  onDismissOutcome,
  onClear,
  onPublish,
  onUnpublish,
  onDiscard,
}: InsightBulkActionBarProps) => {

  const severity = outcome?.kind === 'SUCCESS' ? 'success'
                 : outcome?.kind === 'PARTIAL' ? 'warning'
                 : 'error'

  return (
    <>
      {/* Survives the selection being cleared: a bulk action clears the
          selection on completion, and hiding the result with it would leave the
          user with no idea what happened. */}
      {outcome && (
        <Alert severity={severity} sx={{ mb : 1.5 }} onClose={onDismissOutcome}>
          <AlertTitle sx={{ mb : outcome.failures.length ? undefined : 0 }}>
            {outcome.message}
          </AlertTitle>
          {outcome.failures.map((failure) => (
            <Typography key={failure.key} variant="caption" sx={{ display : 'block' }}>
              <strong>{failure.key}</strong>: {failure.message}
            </Typography>
          ))}
        </Alert>
      )}

      {!!selectedCount && (
        <Alert
          severity="info"
          icon={false}
          sx={{ mb : 1.5, py : 0.5, '& .MuiAlert-message' : { width : '100%' } }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width : '100%' }}>
            <Typography variant="body2" sx={{ fontWeight : 600 }}>
              {selectedCount} selected
            </Typography>

            {isRunning && <CircularProgress size={14} />}

            <Box sx={{ flexGrow : 1 }} />

            <PermissionGate require={UI_PAGES.NBI_INSIGHTS.ctas.publish.id}>
              <Button
                size="small" startIcon={<PublishIcon />}
                disabled={!onPublish || isRunning} onClick={onPublish}
              >
                Publish
              </Button>
            </PermissionGate>

            <PermissionGate require={UI_PAGES.NBI_INSIGHTS.ctas.unpublish.id}>
              <Button
                size="small" startIcon={<UnpublishedIcon />}
                disabled={!onUnpublish || isRunning} onClick={onUnpublish}
              >
                Unpublish
              </Button>
            </PermissionGate>

            <PermissionGate require={UI_PAGES.NBI_INSIGHTS.ctas.discard.id}>
              <Button
                size="small" color="error" startIcon={<DeleteSweepIcon />}
                disabled={!onDiscard || isRunning} onClick={onDiscard}
              >
                Discard drafts
              </Button>
            </PermissionGate>

            <Button size="small" disabled={isRunning} onClick={onClear}>Clear</Button>
          </Stack>
        </Alert>
      )}
    </>
  )
}

export default InsightBulkActionBar
